/**
 * The square part of a picture a person keeps as their avatar (F14): the picture covers a
 * square viewport, and zoom and drag choose which part shows. Stateless; the editor keeps
 * the frame and the browser's canvas draws the result.
 */

export interface PictureSize {
  readonly width: number;
  readonly height: number;
}

/** Where the picture sits in the viewport: its scale over "just covering", and its top left corner in viewport pixels. */
export interface CropFrame {
  readonly zoom: number;
  readonly left: number;
  readonly top: number;
}

/** The kept part, in the picture's own pixels. */
export interface SourceSquare {
  readonly left: number;
  readonly top: number;
  readonly size: number;
}

export const MINIMUM_ZOOM = 1;
export const MAXIMUM_ZOOM = 4;

/** The picture's pixels per viewport pixel at `zoom`. */
export function displayScale(picture: PictureSize, viewport: number, zoom: number): number {
  return (viewport / Math.min(picture.width, picture.height)) * zoom;
}

/** Centred, just covering the viewport. */
export function initialFrame(picture: PictureSize, viewport: number): CropFrame {
  const scale = displayScale(picture, viewport, MINIMUM_ZOOM);
  return { zoom: MINIMUM_ZOOM, left: (viewport - picture.width * scale) / 2, top: (viewport - picture.height * scale) / 2 };
}

/** Zoom about the viewport's centre, so what is in the middle stays there. */
export function zoomedFrame(frame: CropFrame, picture: PictureSize, viewport: number, zoom: number): CropFrame {
  const scale = displayScale(picture, viewport, frame.zoom);
  const centreX = (viewport / 2 - frame.left) / scale;
  const centreY = (viewport / 2 - frame.top) / scale;
  const nextZoom = Math.min(MAXIMUM_ZOOM, Math.max(MINIMUM_ZOOM, zoom));
  const nextScale = displayScale(picture, viewport, nextZoom);
  return framed(picture, viewport, nextZoom, viewport / 2 - centreX * nextScale, viewport / 2 - centreY * nextScale);
}

/** Drag by viewport pixels; the picture never leaves a gap in the viewport. */
export function movedFrame(frame: CropFrame, picture: PictureSize, viewport: number, deltaX: number, deltaY: number): CropFrame {
  return framed(picture, viewport, frame.zoom, frame.left + deltaX, frame.top + deltaY);
}

export function sourceSquare(frame: CropFrame, picture: PictureSize, viewport: number): SourceSquare {
  const scale = displayScale(picture, viewport, frame.zoom);
  // A frame's corner is never right of or below the viewport's, so it is at most 0.
  return { left: Math.abs(frame.left) / scale, top: Math.abs(frame.top) / scale, size: viewport / scale };
}

/** `left` and `top` clamped so the picture covers the viewport. */
function framed(picture: PictureSize, viewport: number, zoom: number, left: number, top: number): CropFrame {
  const scale = displayScale(picture, viewport, zoom);
  return { zoom, left: clamp(left, viewport - picture.width * scale, 0), top: clamp(top, viewport - picture.height * scale, 0) };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
